import {
  IQuoteOfferingRespSchema,
  OnQuoteOfferingChangeAction,
  QuoteOfferingWithChildren,
  QuoteStatusEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { useQueryClient } from '@tanstack/react-query';
import update from 'immutability-helper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cpqServiceQueryKeys } from '../../../../api/cpqService';
import { ensureArray } from '../../../../api/utils';
import { MBox, MFlex } from '../../../../components/Monetize';
import { ExpandCollapseButton } from '../../../../components/Monetize/ExpandCollapseButton';
import { useAccountHierarchyEnabled } from '../../../../hooks/useAccountHierarchyEnabled';
import { logger } from '../../../../services/logger';
import {
  getEmptyQuoteOfferingGroups,
  getQuoteOfferingGroupPermissions,
  getQuoteOfferingsWithChildren,
  isQuoteOfferingAddedToAmendment,
  planQuoteOfferingGroupContainers,
} from '../../../../utils/quotes';
import { ManageQuoteOfferingGroupsLauncher } from '../../Quote/components/manageQuoteOfferingGroups/ManageQuoteOfferingGroupsLauncher';
import QuoteOffering from '../../Quote/components/quoteOffering/QuoteOffering';
import { QuoteOfferingGroupContainer } from '../../Quote/components/quoteOfferingGroup/QuoteOfferingGroupContainer';
import { QuoteOfferingUngroupedContainer } from '../../Quote/components/quoteOfferingGroup/QuoteOfferingUngroupedContainer';
import { useQuoteContext } from '../../Quote/quoteContext';
import { QuoteFormOfferingRow } from '../../Quote/QuoteFormOfferingRow';
import { QuoteFormOfferingTableContent } from '../../Quote/QuoteFormOfferingTableContent';
import { QuoteFormOfferingTableHeader } from '../../Quote/QuoteFormOfferingTableHeader';

/** Key the Ungrouped container's collapse state lives under */
const UNGROUPED_CONTAINER_KEY = 'ungrouped';

/**
 * The amendment layout renders the offerings in two sections — "Existing
 * Offerings" and "New Offerings" — so a group holding both kinds gets a
 * container in each. Section-scoped keys keep their collapse state and their
 * test ids apart; every other layout renders one unprefixed section.
 */
const getContainerKey = (sectionKey: string, id: string) =>
  sectionKey ? `${sectionKey}-${id}` : id;

type OfferingSection = {
  /** Namespaces collapse state and test ids — see getContainerKey */
  key: string;
  /** Qualifies the containers' collapse-control accessible names */
  label?: string;
  offerings: QuoteOfferingWithChildren[];
  /**
   * Whether a group with no offerings anywhere on the quote renders its empty
   * container here. Only one section may claim them, and it has to be a section
   * offerings can be added to — an empty group exists to be filled or deleted.
   */
  hostsEmptyGroups: boolean;
  canAddOfferings: boolean;
};

export const QuoteInternalOfferingTable = () => {
  const {
    quoteData,
    quotePrices,
    useAmendmentV2,
    isReadOnly,
    quoteOfferingGroups,
  } = useQuoteContext();
  const { quote, fetchQuote, evaluateQuoteRules, reorderQuoteOfferings } =
    quoteData;
  const queryClient = useQueryClient();

  const groups = quoteOfferingGroups?.groups;
  const hasGroups = !!groups?.length;

  const groupPermissions = useMemo(
    () => getQuoteOfferingGroupPermissions({ quote, isReadOnly }),
    [quote, isReadOnly],
  );

  /**
   * Groups with no offerings anywhere on the quote — the only ones allowed to
   * render as an empty container. Computed against the whole quote rather than a
   * section's slice: the amendment layout splits offerings across two sections,
   * and a group whose members all sit in the other section is not empty.
   *
   * Only on a DRAFT: an empty container exists to be filled or deleted, and
   * neither is actionable once the quote has moved on.
   */
  const emptyGroupIds = useMemo(() => {
    if (quote?.status !== QuoteStatusEnum.DRAFT) {
      return new Set<string>();
    }
    return new Set(
      getEmptyQuoteOfferingGroups(groups, quote?.quoteOfferings).map(
        ({ id }) => id,
      ),
    );
  }, [groups, quote?.quoteOfferings, quote?.status]);

  const isAccountHierarchyEnabled = useAccountHierarchyEnabled(
    quote?.accountId,
  );
  /**
   * Manage Groups is offered on every quote status and type — read-only quotes
   * open the modal in review mode rather than losing access to it. Groups only
   * mean anything under an account hierarchy, so a tenant without one only sees
   * the button on a quote that already has groups.
   */
  const showManageGroups = isAccountHierarchyEnabled || hasGroups;

  const getQuoteOfferingsWithChildrenInternal = (
    quoteOfferings: IQuoteOfferingRespSchema[],
  ) => {
    const quoteOfferingsWithChildren =
      getQuoteOfferingsWithChildren(quoteOfferings);

    if (useAmendmentV2) {
      return quoteOfferingsWithChildren.sort((a, b) => {
        return (
          (isQuoteOfferingAddedToAmendment(a.quoteOffering) ? 1 : -1) -
          (isQuoteOfferingAddedToAmendment(b.quoteOffering) ? 1 : -1)
        );
      });
    } else {
      return quoteOfferingsWithChildren;
    }
  };
  const onQuoteOfferingChange = useCallback(
    async (
      action: OnQuoteOfferingChangeAction,
      /**
       * One or more quote offerings to work with
       * Delete is the only use-case where there are multiple offerings
       */
      quoteOfferings?: Maybe<
        IQuoteOfferingRespSchema | IQuoteOfferingRespSchema[]
      >,
      oldQuoteOfferingId?: string,
      skipFetch = false,
      runEvaluate = false,
    ) => {
      if (quote?.id) {
        try {
          // Update in-memory quote so page updates instantly while quote is re-fetched
          if (quoteOfferings) {
            let tempQuoteOfferings = [
              ...(quoteData.quote?.quoteOfferings || []),
            ];

            ensureArray(quoteOfferings).forEach((quoteOffering) => {
              switch (action) {
                case 'ADD':
                  tempQuoteOfferings = [...tempQuoteOfferings, quoteOffering];
                  break;
                case 'DELETE':
                  tempQuoteOfferings = tempQuoteOfferings.filter(
                    ({ id, parentQuoteOfferingId }) =>
                      id !== quoteOffering.id ||
                      parentQuoteOfferingId !== quoteOffering.id,
                  );
                  break;
                case 'UPDATE':
                  tempQuoteOfferings = tempQuoteOfferings.map((currItem) =>
                    currItem.id === quoteOffering.id ? quoteOffering : currItem,
                  );
                  break;
                case 'REPLACE':
                  // Remove oldQuoteOfferingId, then add the new offering to the end
                  if (oldQuoteOfferingId) {
                    tempQuoteOfferings = [
                      ...tempQuoteOfferings.filter(
                        ({ id }) => id !== oldQuoteOfferingId,
                      ),
                      quoteOffering,
                    ];
                  }
                  break;
                default:
                  break;
              }
            });

            setOrderedQuoteOfferings(
              getQuoteOfferingsWithChildrenInternal(tempQuoteOfferings),
            );
          }
        } catch (ex) {
          // will self-resolve after re-fetching quote
          logger.warn('Error setting quote offerings', ex);
        }
        if (action === 'DELETE') {
          // group membership may reference the deleted offering
          queryClient.invalidateQueries({
            queryKey: [...cpqServiceQueryKeys.quoteOfferingGroups(quote.id)],
          });
        }
        if (!skipFetch) {
          await (runEvaluate
            ? evaluateQuoteRules(quote.id)
            : fetchQuote(quote.id));
        }
      }
    },
    [
      evaluateQuoteRules,
      fetchQuote,
      quote,
      quoteData.quote?.quoteOfferings,
      queryClient,
    ],
  );

  // Seeded from the quote so the first paint already has rows; the effect
  // below keeps it in step with later quote updates.
  const [orderedQuoteOfferings, setOrderedQuoteOfferings] = useState<
    QuoteOfferingWithChildren[]
  >(() =>
    getQuoteOfferingsWithChildrenInternal(
      quoteData.quote?.quoteOfferings || [],
    ),
  );
  // used to determine if order actually changed before calling API
  const [priorQuoteOfferingOrder, setPriorQuoteOfferingOrder] = useState<
    string[]
  >(() => orderedQuoteOfferings.map(({ quoteOffering }) => quoteOffering?.id));

  const [offeringOpenStates, setOfferingOpenStates] = useState<
    Record<string, boolean>
  >({});
  /**
   * Group containers collapse alongside the offerings inside them. Keyed by
   * group id plus UNGROUPED_CONTAINER_KEY for the Ungrouped box; absent means
   * open, so a group added while the page is up starts expanded.
   */
  const [containerOpenStates, setContainerOpenStates] = useState<
    Record<string, boolean>
  >({});

  const handleOfferingOpenChange = useCallback(
    (id: string, isOpen: boolean) => {
      setOfferingOpenStates((prev) => ({ ...prev, [id]: isOpen }));
    },
    [],
  );

  const handleContainerOpenChange = useCallback(
    (key: string, isOpen: boolean) => {
      setContainerOpenStates((prev) => ({ ...prev, [key]: isOpen }));
    },
    [],
  );

  /**
   * The runs of offerings the table renders, in order. The amendment layout puts
   * the contract's offerings and the amendment's own in separate sections;
   * everything else is one section. Empty groups belong to the section offerings
   * are added to, which on an amendment is "New Offerings".
   */
  const offeringSections = useMemo<OfferingSection[]>(() => {
    if (!useAmendmentV2) {
      return [
        {
          key: '',
          offerings: orderedQuoteOfferings,
          hostsEmptyGroups: true,
          canAddOfferings: true,
        },
      ];
    }
    return [
      {
        key: 'existing',
        label: 'Existing Offerings',
        offerings: orderedQuoteOfferings.filter(
          ({ quoteOffering }) =>
            !isQuoteOfferingAddedToAmendment(quoteOffering),
        ),
        hostsEmptyGroups: false,
        canAddOfferings: false,
      },
      {
        key: 'new',
        label: 'New Offerings',
        offerings: orderedQuoteOfferings.filter(({ quoteOffering }) =>
          isQuoteOfferingAddedToAmendment(quoteOffering),
        ),
        hostsEmptyGroups: true,
        canAddOfferings: true,
      },
    ];
  }, [useAmendmentV2, orderedQuoteOfferings]);

  /**
   * Every container the table actually renders — group boxes plus Ungrouped, per
   * section. Derived from the same planner the render pass uses so expand /
   * collapse all can't count containers that aren't on screen.
   */
  const containerKeys = useMemo(() => {
    if (!hasGroups) {
      return [];
    }
    return offeringSections.flatMap(({ key, offerings, hostsEmptyGroups }) => {
      const { groupPartitions, ungroupedOfferings } =
        planQuoteOfferingGroupContainers({
          offerings,
          groups,
          emptyGroupIds: hostsEmptyGroups ? emptyGroupIds : undefined,
        });
      return [
        ...groupPartitions.map(({ group }) => getContainerKey(key, group.id)),
        ...(ungroupedOfferings.length
          ? [getContainerKey(key, UNGROUPED_CONTAINER_KEY)]
          : []),
      ];
    });
  }, [groups, hasGroups, offeringSections, emptyGroupIds]);

  const areAllOfferingOpen =
    orderedQuoteOfferings
      .filter(({ quoteOffering }) => !!quoteOffering?.id)
      .every(
        ({ quoteOffering }) => offeringOpenStates[quoteOffering.id] !== false,
      ) && containerKeys.every((key) => containerOpenStates[key] !== false);

  // incremented on expand all so every offering also opens its rate sections
  const [expandRatesSignal, setExpandRatesSignal] = useState(0);

  const setAllOpenStates = useCallback(
    (isOpen: boolean) => {
      setOfferingOpenStates(
        Object.fromEntries(
          orderedQuoteOfferings
            .filter(({ quoteOffering }) => !!quoteOffering?.id)
            .map(({ quoteOffering }) => [quoteOffering.id, isOpen]),
        ),
      );
      setContainerOpenStates(
        Object.fromEntries(containerKeys.map((key) => [key, isOpen])),
      );
    },
    [orderedQuoteOfferings, containerKeys],
  );

  const expandAll = useCallback(() => {
    setAllOpenStates(true);
    setExpandRatesSignal((prev) => prev + 1);
  }, [setAllOpenStates]);

  const collapseAll = useCallback(
    () => setAllOpenStates(false),
    [setAllOpenStates],
  );

  // Convert quoteOfferings into nested structure with children
  useEffect(() => {
    const quoteOfferingWithChildren = getQuoteOfferingsWithChildrenInternal(
      quoteData.quote?.quoteOfferings || [],
    );
    setOrderedQuoteOfferings(quoteOfferingWithChildren);
    setPriorQuoteOfferingOrder(
      quoteOfferingWithChildren.map(({ quoteOffering }) => quoteOffering?.id),
    );
  }, [quoteData.quote?.quoteOfferings]);

  const saveMoveOffering = useCallback(() => {
    // TODO: call backend API to save the new order
    // should detect if order actually changed
    // child quote offerings should be moved with their parent (not sure if BE considers children)
    const newOfferingOrder = orderedQuoteOfferings.map(
      ({ quoteOffering }) => quoteOffering.id,
    );
    const didChange = newOfferingOrder.some(
      (id, i) => priorQuoteOfferingOrder[i] !== id,
    );
    if (didChange && quoteData.quote) {
      // call api to save new order
      reorderQuoteOfferings(quoteData.quote.id, newOfferingOrder);
    }

    quoteData?.quote?.id &&
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quotePrices(quoteData?.quote?.id),
      });
  }, [
    orderedQuoteOfferings,
    priorQuoteOfferingOrder,
    quoteData.quote,
    reorderQuoteOfferings,
  ]);

  const moveOffering = useCallback((dragIndex: number, hoverIndex: number) => {
    try {
      setOrderedQuoteOfferings((prevQuoteOfferings) =>
        update(prevQuoteOfferings, {
          $splice: [
            [dragIndex, 1],
            [
              hoverIndex,
              0,
              prevQuoteOfferings[dragIndex] as QuoteOfferingWithChildren,
            ],
          ],
        }),
      );
    } catch (ex) {
      logger.warn('Error reordering quote offerings', ex);
    }
  }, []);

  const renderQuoteOfferings = useCallback(
    (
      quoteOfferings: QuoteOfferingWithChildren[],
      baseDragIndex = 0,
      baseIndex = 0,
    ) => {
      let currIndex = 0;
      return quoteOfferings.map(({ quoteOffering, children }, i) => {
        const index = currIndex;
        currIndex++;
        if (children?.length) {
          currIndex += children.length;
        }

        return (
          <QuoteFormOfferingRow
            dragIndex={baseDragIndex + i}
            index={baseIndex + index}
            key={quoteOffering.id}
            quote={quote}
            quoteOffering={quoteOffering}
            childQuoteOfferings={children}
            quotePrices={quotePrices}
            onChange={onQuoteOfferingChange}
            moveOffering={moveOffering}
            saveMoveOffering={saveMoveOffering}
            quoteOfferings={quoteOfferings}
            isOfferingOpen={offeringOpenStates[quoteOffering.id]}
            onIsOfferingOpenChange={(isOpen) =>
              handleOfferingOpenChange(quoteOffering.id, isOpen)
            }
            expandRatesSignal={expandRatesSignal}
            isDraggable={!hasGroups}
          />
        );
      });
    },
    [
      moveOffering,
      onQuoteOfferingChange,
      quote,
      quotePrices,
      saveMoveOffering,
      offeringOpenStates,
      handleOfferingOpenChange,
      expandRatesSignal,
      hasGroups,
    ],
  );

  /**
   * Same signature as renderQuoteOfferings so it can drop into the table
   * content render prop. When groups exist, group containers render first
   * (in groups[] order), followed by the remaining offerings in their own
   * Ungrouped container. Drag-reorder is disabled in that state, so the
   * per-partition drag indexes are informational only.
   *
   * Empty group containers only render on DRAFT quotes — they exist to be
   * filled or deleted, which is only actionable while editing.
   */
  const renderPartitionedQuoteOfferings = useCallback(
    (
      quoteOfferings: QuoteOfferingWithChildren[],
      baseDragIndex = 0,
      baseIndex = 0,
      section: Pick<
        OfferingSection,
        'key' | 'label' | 'hostsEmptyGroups' | 'canAddOfferings'
      > = {
        key: '',
        hostsEmptyGroups: true,
        canAddOfferings: true,
      },
    ) => {
      if (!groups?.length) {
        return renderQuoteOfferings(quoteOfferings, baseDragIndex, baseIndex);
      }

      const {
        key: sectionKey,
        label: sectionLabel,
        hostsEmptyGroups,
        canAddOfferings,
      } = section;
      const groupIndexById = new Map(
        groups.map(({ id }, index) => [id, index]),
      );
      const { groupPartitions, ungroupedOfferings } =
        planQuoteOfferingGroupContainers({
          offerings: quoteOfferings,
          groups,
          emptyGroupIds: hostsEmptyGroups ? emptyGroupIds : undefined,
        });

      const rendered: JSX.Element[] = [];
      let dragIndexOffset = baseDragIndex;
      let indexOffset = baseIndex;
      const advanceOffsets = (offerings: QuoteOfferingWithChildren[]) => {
        dragIndexOffset += offerings.length;
        indexOffset +=
          offerings.length +
          offerings.reduce((sum, { children }) => sum + children.length, 0);
      };

      groupPartitions.forEach(({ group, offerings }) => {
        const rows = renderQuoteOfferings(
          offerings,
          dragIndexOffset,
          indexOffset,
        );
        advanceOffsets(offerings);
        const containerKey = getContainerKey(sectionKey, group.id);
        rendered.push(
          <QuoteOfferingGroupContainer
            key={containerKey}
            containerKey={containerKey}
            sectionLabel={sectionLabel}
            group={group}
            groupNumber={(groupIndexById.get(group.id) ?? 0) + 1}
            groupOfferings={offerings}
            quotePrices={quotePrices}
            quoteOfferings={orderedQuoteOfferings}
            onChange={onQuoteOfferingChange}
            permissions={groupPermissions}
            canAddOfferings={canAddOfferings}
            isOpen={containerOpenStates[containerKey] !== false}
            onIsOpenChange={(isOpen) =>
              handleContainerOpenChange(containerKey, isOpen)
            }
          >
            {rows}
          </QuoteOfferingGroupContainer>,
        );
      });

      if (ungroupedOfferings.length) {
        const containerKey = getContainerKey(
          sectionKey,
          UNGROUPED_CONTAINER_KEY,
        );
        rendered.push(
          <QuoteOfferingUngroupedContainer
            key={containerKey}
            containerKey={containerKey}
            sectionLabel={sectionLabel}
            offerings={ungroupedOfferings}
            isOpen={containerOpenStates[containerKey] !== false}
            onIsOpenChange={(isOpen) =>
              handleContainerOpenChange(containerKey, isOpen)
            }
          >
            {renderQuoteOfferings(
              ungroupedOfferings,
              dragIndexOffset,
              indexOffset,
            )}
          </QuoteOfferingUngroupedContainer>,
        );
      }

      return rendered;
    },
    [
      groups,
      renderQuoteOfferings,
      quotePrices,
      orderedQuoteOfferings,
      onQuoteOfferingChange,
      emptyGroupIds,
      groupPermissions,
      containerOpenStates,
      handleContainerOpenChange,
    ],
  );

  if (useAmendmentV2) {
    const [existingSection, newSection] = offeringSections;
    const baseDragIndex = existingSection.offerings.length;
    const baseIndex =
      baseDragIndex +
      existingSection.offerings.reduce(
        (sum, { children }) => sum + children.length,
        0,
      );

    return (
      <>
        {(orderedQuoteOfferings.length >= 2 || showManageGroups) && (
          <MFlex justify="space-between" align="center" mb={2}>
            <MBox>
              {orderedQuoteOfferings.length >= 2 && (
                <ExpandCollapseButton
                  isExpanded={areAllOfferingOpen}
                  onChange={(expanded) =>
                    expanded ? expandAll() : collapseAll()
                  }
                />
              )}
            </MBox>
            {showManageGroups && <ManageQuoteOfferingGroupsLauncher />}
          </MFlex>
        )}

        <MBox>
          <QuoteFormOfferingTableHeader
            mt="2"
            offeringTitle="Existing Offerings"
          />
          {/*
           * Offerings carried over from the contract: their groups are shown
           * for reference, but nothing is added here — new offerings go in the
           * section below, and so do empty groups.
           */}
          {renderPartitionedQuoteOfferings(
            existingSection.offerings,
            0,
            0,
            existingSection,
          )}
        </MBox>

        <MBox>
          <QuoteFormOfferingTableHeader mt="8" offeringTitle="New Offerings" />
          {renderPartitionedQuoteOfferings(
            newSection.offerings,
            baseDragIndex,
            baseIndex,
            newSection,
          )}
          {quote?.status === QuoteStatusEnum.DRAFT && quote?.id && (
            <QuoteOffering
              quotePrices={quotePrices}
              onChange={onQuoteOfferingChange}
              index={-1}
              quoteOfferings={orderedQuoteOfferings}
            />
          )}
        </MBox>
      </>
    );
  }

  return (
    <>
      {(orderedQuoteOfferings.length >= 2 || showManageGroups) && (
        <MFlex justify="space-between" align="center" mb={2}>
          <MBox>
            {orderedQuoteOfferings.length >= 2 && (
              <ExpandCollapseButton
                isExpanded={areAllOfferingOpen}
                onChange={(expanded) =>
                  expanded ? expandAll() : collapseAll()
                }
              />
            )}
          </MBox>
          {showManageGroups && <ManageQuoteOfferingGroupsLauncher />}
        </MFlex>
      )}
      <QuoteFormOfferingTableContent
        quoteOfferings={orderedQuoteOfferings}
        quote={quote}
        onChange={onQuoteOfferingChange}
        quotePrices={quotePrices}
        renderQuoteOfferings={renderPartitionedQuoteOfferings}
        canAddNewOffering={!isReadOnly}
      />
    </>
  );
};
